// src/pages/ArenaPage.jsx
// Online PvP: Realtime Database üzerinden eşleşme ve hamle senkronizasyonu. Motor yalnızca
// yasal hamlelere izin verdiği için oyun, hamleden sonra rakibin yasal hamlesi kalmadığında
// (mat) biter.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import alertify from '../lib/alertifySetup';
import PhaserBoard from '../components/PhaserBoard';
import HandRow from '../components/HandRow';
import Seo, { breadcrumbLd } from '../components/Seo';
import useBoardInteraction from '../components/useBoardInteraction';
import { Shogi, PIECE_NAMES, PROMOTED_NAMES } from '../lib/ShogiEngine';
import { findOrCreateMatch, subscribeToRoom, sendOnlineMove, finishOnlineMatch } from '../lib/arenaStorage';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../i18n';

const T = {
  tr: {
    title: 'Şogi Arena — Online Şogi Oyna',
    h1: 'Şogi Arena',
    seoDesc: 'Gerçek oyunculara karşı online şogi oyna. Hızlı eşleşme, canlı tahta, ücretsiz.',
    lead: 'Gerçek bir rakiple eşleş ve canlı oyna. Eşleşme genellikle saniyeler sürer.',
    find: 'Hızlı Maç Bul',
    searching: 'Rakip aranıyor…',
    searchingNote: 'Bu sekmeyi açık tut; rakip bulunduğunda oyun otomatik başlar.',
    found: 'Maç bulundu! Tarafın: {c}',
    side: 'Tarafın: {c}',
    yourTurn: 'Sıra sende',
    theirTurn: 'Rakip düşünüyor…',
    opponent: 'Rakip',
    you: 'Sen',
    won: 'Kazandın! 🎉',
    lost: 'Kaybettin.',
    reasonMate: 'Şah mat.',
    again: 'Yeni Maç',
    guestNote: 'Misafir olarak oynuyorsun. Giriş yaparsan rakibin takma adını görür.',
    practice: 'Rakip bulunamazsa bilgisayara karşı pratik yap',
    rulesTitle: 'Arena kuralları',
    rules: ['Taraflar rastgele belirlenir; Sente ilk hamleyi yapar.', 'Sadece yasal hamleler oynanabilir; mat eden kazanır.', 'Sayfayı kapatırsan bekleme listesinden çıkarsın.'],
  },
  en: {
    title: 'Shogi Arena — Play Shogi Online',
    h1: 'Shogi Arena',
    seoDesc: 'Play shogi online against real players. Quick matchmaking, live board, free.',
    lead: 'Get matched with a real opponent and play live. Matching usually takes seconds.',
    find: 'Find a Match',
    searching: 'Looking for an opponent…',
    searchingNote: 'Keep this tab open; the game starts automatically when an opponent is found.',
    found: 'Match found! You are {c}',
    side: 'You are {c}',
    yourTurn: 'Your move',
    theirTurn: 'Opponent is thinking…',
    opponent: 'Opponent',
    you: 'You',
    won: 'You won! 🎉',
    lost: 'You lost.',
    reasonMate: 'Checkmate.',
    again: 'New Match',
    guestNote: 'You are playing as a guest. Sign in so your opponent sees your nickname.',
    practice: 'No opponent? Practise against the computer',
    rulesTitle: 'Arena rules',
    rules: ['Sides are assigned at random; Sente moves first.', 'Only legal moves can be played; checkmate wins.', 'Closing the page removes you from the queue.'],
  },
};

export default function ArenaPage() {
  const { user } = useAuth();
  const { lang, to, t } = useLang();
  const tx = T[lang];

  const engineRef = useRef(null);
  if (!engineRef.current) engineRef.current = new Shogi();
  const engine = engineRef.current;

  const [matching, setMatching] = useState(false);
  const [roomId, setRoomId] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [snap, setSnap] = useState({ board: engine.board(), hands: engine.hands(), lastMove: null });

  const guestId = useMemo(() => `guest_${Math.random().toString(36).substring(2, 7)}`, []);
  const colorLabel = (c) => (c === 'sente' ? t('common.senteLong') : t('common.goteLong'));

  const sync = useCallback((lastMove = null) => {
    setSnap({ board: engine.board(), hands: engine.hands(), lastMove });
  }, [engine]);

  const handleFindMatch = () => {
    setMatching(true);
    engine.reset();
    sync(null);
    setRoomData(null);
    const playerProfile = {
      uid: user?.uid || guestId,
      nickname: user?.displayName || user?.email?.split('@')[0] || t('auth.player'),
      photoURL: user?.photoURL || '',
    };
    findOrCreateMatch(playerProfile, ({ roomId: id, role }) => {
      setRoomId(id);
      setMyRole(role);
      setMatching(false);
      alertify.success(tx.found.replace('{c}', colorLabel(role)));
    });
  };

  // Odayı canlı dinle; rakibin hamlesini yerel motora uygula
  useEffect(() => {
    if (!roomId) return undefined;
    const unsubscribe = subscribeToRoom(roomId, (data) => {
      if (!data) return;
      setRoomData(data);
      const lm = data.lastMove;
      if (!lm) return;
      const history = engine.history();
      const last = history[history.length - 1];
      const same =
        last &&
        last.type === lm.type &&
        last.color === lm.color &&
        last.to?.row === lm.to?.row &&
        last.to?.col === lm.to?.col &&
        last.from?.row === lm.from?.row &&
        last.from?.col === lm.from?.col;
      if (same) return;
      if (lm.type === 'drop') {
        engine.drop({ type: lm.pieceType || lm.piece, row: lm.to.row, col: lm.to.col, color: lm.color });
      } else {
        engine.move({ from: lm.from, to: lm.to, promote: !!lm.promoted });
      }
      sync(lm);
    });
    return () => unsubscribe();
  }, [roomId, engine, sync]);

  const playing = roomData?.status === 'playing';
  const myTurn = playing && roomData?.turn === myRole;

  const onMove = useCallback(async (descriptor) => {
    const record = engine.applyDescriptor(descriptor);
    sync(record);
    const nextTurn = myRole === 'sente' ? 'gote' : 'sente';
    await sendOnlineMove(roomId, record, nextTurn);
    if (!engine.hasAnyLegalMoves(nextTurn)) {
      await finishOnlineMatch(roomId, myRole, 'checkmate');
    }
  }, [engine, sync, myRole, roomId]);

  const interaction = useBoardInteraction(engine, { color: myRole, canAct: myTurn, onMove });

  const opponentRole = myRole === 'sente' ? 'gote' : 'sente';
  const opponentName = roomData?.players?.[opponentRole]?.nickname || tx.opponent;

  return (
    <>
      <Seo
        title={tx.title}
        description={tx.seoDesc}
        jsonLd={breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: tx.h1, path: to('arena') }])}
      />
      <div className="container py-3 py-md-4">
        <h1 className="mb-1" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.6rem)' }}>{tx.h1}</h1>
        <p className="text-muted mb-3">{tx.lead}</p>

        {!roomId ? (
          <div className="row g-3">
            <div className="col-12 col-lg-7">
              <div className="game-card text-center align-items-center" style={{ '--c': 'var(--c-arena)' }}>
                <span className="icon jp">対</span>
                <button type="button" className="btn btn-ink btn-lg mt-2" onClick={handleFindMatch} disabled={matching}>
                  {matching ? (<><span className="spinner-border spinner-border-sm me-2" />{tx.searching}</>) : tx.find}
                </button>
                {matching && <p className="small text-muted mt-3 mb-0">{tx.searchingNote}</p>}
                {!user && <p className="small text-muted mt-3 mb-0">{tx.guestNote}</p>}
                <Link to={to('play')} className="link-arrow mt-3 small">{tx.practice}</Link>
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <section className="panel prose h-100">
                <h2 className="h4">{tx.rulesTitle}</h2>
                <ul className="mb-0">{tx.rules.map((r) => <li key={r}>{r}</li>)}</ul>
              </section>
            </div>
          </div>
        ) : (
          <div className="row g-3 justify-content-center">
            <div className="col-12 col-lg-8">
              <div className="card">
                <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <span className={`status-pill turn-${myRole}`}>{tx.side.replace('{c}', colorLabel(myRole))}</span>
                  {playing && (
                    <span className="status-pill">
                      {myTurn ? tx.yourTurn : (<><span className="spinner-border spinner-border-sm" /> {tx.theirTurn}</>)}
                    </span>
                  )}
                </div>
                <div className="card-body d-flex flex-column gap-2 p-2 p-sm-3">
                  {roomData?.status === 'finished' && (
                    <div className="alert alert-success text-center mb-1 py-2">
                      <div className="fw-bold fs-5">{roomData.winner === myRole ? tx.won : tx.lost}</div>
                      <div className="small mb-2">{tx.reasonMate}</div>
                      <button type="button" className="btn btn-sm btn-ink" onClick={() => { setRoomId(null); setRoomData(null); }}>{tx.again}</button>
                    </div>
                  )}
                  <HandRow label={`${opponentName} · ${colorLabel(opponentRole)}`} hand={snap.hands[opponentRole]} />
                  <div className="board-wrap">
                    <PhaserBoard
                      rows={9}
                      cols={9}
                      cellSize={64}
                      board={snap.board}
                      lastMove={snap.lastMove}
                      pieceLabels={PIECE_NAMES}
                      promotedLabels={PROMOTED_NAMES}
                      selected={interaction.selected}
                      legalTargets={interaction.legalTargets}
                      onSquareClick={interaction.handleSquareClick}
                      flipBoard={myRole === 'gote'}
                    />
                  </div>
                  <HandRow
                    label={`${tx.you} · ${colorLabel(myRole)}`}
                    hand={snap.hands[myRole]}
                    active={myTurn}
                    dropType={interaction.dropType}
                    onPick={interaction.handleHandClick}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
